import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import BlastUploadDialog from './BlastUploadDialog';
import useMusicSync from '../../hooks/useMusicSync';

const THUMB = 100;

const PlaceBlastsComponent = ({ freakPlaceId, currentUser, canModify }) => {
  const [reels, setReels] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const videoRef = useRef(null);

  const load = async () => {
    try {
      const res = await agent.Reels.byFreakPlace(freakPlaceId);
      setReels(res.reels || []);
    } catch {}
  };

  useEffect(() => { load(); }, [freakPlaceId]);

  const openAt = (i) => { setActiveIndex(i); setPlayerOpen(true); };
  const closePlayer = () => setPlayerOpen(false);
  const prev = () => setActiveIndex(i => Math.max(0, i - 1));
  const next = () => setActiveIndex(i => Math.min(reels.length - 1, i + 1));

  const handleLike = async (reel) => {
    try {
      if (reel.isLiked) {
        await agent.Reels.unlike(reel.slug);
        setReels(prev => prev.map(r => r.slug === reel.slug
          ? { ...r, isLiked: false, _count: { ...r._count, likes: Math.max(0, (r._count?.likes ?? 1) - 1) } }
          : r));
      } else {
        await agent.Reels.like(reel.slug);
        setReels(prev => prev.map(r => r.slug === reel.slug
          ? { ...r, isLiked: true, _count: { ...r._count, likes: (r._count?.likes ?? 0) + 1 } }
          : r));
      }
    } catch {}
  };

  const handleDelete = async (slug, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this blast?')) return;
    try {
      await agent.Reels.delete(slug);
      await load();
    } catch {}
  };

  const canDelete = (reel) =>
    reel.author?.username === currentUser?.username;

  if (reels.length === 0 && !canModify) return null;

  const active = reels[activeIndex];
  const { hasMusic } = useMusicSync(videoRef, active?.musicUrl, active?.musicStartMs || 0);

  return (
    <Box sx={{ mb: 2 }}>
      {/* Label */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, pl: 1.5, borderLeft: `3px solid ${DEFAULT_THEME_COLOR}` }}>
        <LocalFireDepartmentIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 16 }} />
        <Typography sx={{ color: DEFAULT_THEME_COLOR, fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Blasts
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>({reels.length})</Typography>
        {canModify && (
          <IconButton
            size="small"
            onClick={() => setUploadOpen(true)}
            title="Add Blast"
            sx={{ p: 0.4, ml: 0.25, color: DEFAULT_THEME_COLOR, '&:focus': { outline: 'none' } }}
          >
            <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {reels.length === 0 && canModify && (
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', pl: 1.5, mb: 1 }}>
          No blasts yet — tap + to add the first one.
        </Typography>
      )}

      {/* Thumbnail strip */}
      {reels.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { background: `${DEFAULT_THEME_COLOR}44`, borderRadius: 2 } }}>
          {reels.map((reel, i) => (
            <Box
              key={reel.id || reel.slug}
              onClick={() => openAt(i)}
              sx={{
                flexShrink: 0, width: THUMB, height: THUMB, borderRadius: 2, overflow: 'hidden',
                cursor: 'pointer', position: 'relative', background: '#111',
                border: '1px solid rgba(255,255,255,0.1)', transition: 'box-shadow 0.15s',
                '&:hover': { boxShadow: `0 0 0 2px ${DEFAULT_THEME_COLOR}` },
              }}
            >
              {reel.thumbnailUrl
                ? <Box component="img" src={reel.thumbnailUrl} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <Box sx={{ width: '100%', height: '100%', background: '#1e1e1e' }} />
              }
              <PlayCircleIcon sx={{ position: 'absolute', bottom: 4, right: 4, color: 'rgba(255,255,255,0.85)', fontSize: 22 }} />
              {canDelete(reel) && (
                <IconButton
                  size="small"
                  onClick={(e) => handleDelete(reel.slug, e)}
                  sx={{ position: 'absolute', top: 2, right: 2, p: 0.3, background: 'rgba(0,0,0,0.6)', '&:hover': { background: 'rgba(200,0,0,0.75)' } }}
                >
                  <DeleteIcon sx={{ fontSize: 13, color: 'white' }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Fullscreen player */}
      <Dialog fullScreen open={playerOpen} onClose={closePlayer} PaperProps={{ sx: { background: '#000' } }}>
        <Box sx={{ position: 'relative', width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {active && (
            <video
              ref={videoRef}
              key={active.slug}
              src={active.videoUrl}
              autoPlay loop controls playsInline
              style={{ maxWidth: '100%', maxHeight: '100vh', objectFit: 'contain' }}
            />
          )}
          <IconButton onClick={closePlayer} sx={{ position: 'absolute', top: 16, left: 16, background: 'rgba(0,0,0,0.55)', color: 'white', '&:hover': { background: 'rgba(0,0,0,0.85)' } }}>
            <CloseIcon />
          </IconButton>
          {activeIndex > 0 && (
            <IconButton onClick={prev} sx={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.55)', color: 'white', '&:hover': { background: 'rgba(0,0,0,0.85)' } }}>
              <ArrowBackIosNewIcon />
            </IconButton>
          )}
          {activeIndex < reels.length - 1 && (
            <IconButton onClick={next} sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.55)', color: 'white', '&:hover': { background: 'rgba(0,0,0,0.85)' } }}>
              <ArrowForwardIosIcon />
            </IconButton>
          )}
          {active && (
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, transparent 100%)', px: 2.5, pb: 3, pt: 6, pointerEvents: 'none' }}>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>@{active.author?.username}</Typography>
              {active.caption && (
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', mt: 0.5 }}>{active.caption}</Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, pointerEvents: 'auto' }}>
                <IconButton size="small" onClick={() => handleLike(active)} sx={{ color: active.isLiked ? '#f43f5e' : 'rgba(255,255,255,0.65)', p: 0.5 }}>
                  {active.isLiked ? <FavoriteIcon sx={{ fontSize: 22 }} /> : <FavoriteBorderIcon sx={{ fontSize: 22 }} />}
                </IconButton>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem' }}>{active._count?.likes ?? 0}</Typography>
              </Box>
              {hasMusic && active.musicName && (
                <Box
                  onClick={(e) => { e.stopPropagation(); if (active.musicLicenseUrl) window.open(active.musicLicenseUrl, '_blank', 'noopener'); }}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.75, mt: 1, pointerEvents: 'auto',
                    background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 2, px: 1.25, py: 0.4, cursor: active.musicLicenseUrl ? 'pointer' : 'default',
                  }}
                >
                  <MusicNoteIcon sx={{ fontSize: 13, color: 'white' }} />
                  <Typography sx={{ color: 'white', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                    {active.musicName} — {active.musicArtist}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {reels.length > 1 && (
            <Box sx={{ position: 'absolute', bottom: 100, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 0.75 }}>
              {reels.map((_, i) => (
                <Box key={i} onClick={() => setActiveIndex(i)} sx={{ width: i === activeIndex ? 20 : 6, height: 6, borderRadius: 3, background: i === activeIndex ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s' }} />
              ))}
            </Box>
          )}
        </Box>
      </Dialog>

      <BlastUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        freakPlaceId={freakPlaceId}
        onSuccess={() => load()}
      />
    </Box>
  );
};

export default PlaceBlastsComponent;
